import { AccountingDirectoryControlsComponent } from '../../shared/accounting-directory-controls.component';
import { isEditableFinancialDraft } from '../../shared/accounting-draft-eligibility';
// generated-receipts.component.ts
import { Component, OnInit, inject } from '@angular/core';
import { FinancePdfViewButtonComponent } from '../../finance-pdf-view-button.component';
import { AccountsService } from '../../accounts.service';
import { MessageService, MenuItem } from "primeng/api";
import { Router } from "@angular/router";
import { SessionStorageService } from "../../../services/session-storage.service";
import { PaymentService } from "../../../services/payment.service";
import { PaymentTransactions } from "../../payment-transactions/payment-transactions.model";
import { Receipt } from "../../receipt/receipt.model";
import { TableLazyLoadEvent, TableModule } from "primeng/table";
import * as XLSX from "xlsx";
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { MultiSelectModule } from 'primeng/multiselect';
import { DatePickerModule } from 'primeng/datepicker';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { TooltipModule } from 'primeng/tooltip';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ProgressBarModule } from "primeng/progressbar";
import { PanelModule } from "primeng/panel";
import { ImageModule } from "primeng/image";
import { InputGroupModule } from "primeng/inputgroup";
import { InputGroupAddonModule } from "primeng/inputgroupaddon";
import { IftaLabelModule } from "primeng/iftalabel";
import { DrawerModule } from "primeng/drawer";
import { PermissionService } from '../../../security/permission.service';
import { app_permissions } from '../../../security/app-permissions';
import { PageShellComponent } from '../../../shared/page-layout/page-shell.component';
import { PageHeroComponent } from '../../../shared/page-layout/page-hero.component';
import { PageMetricStripComponent } from '../../../shared/page-layout/page-metric-strip.component';
import { PageSearchPanelComponent } from '../../../shared/page-layout/page-search-panel.component';
import { PageDataPanelComponent } from '../../../shared/page-layout/page-data-panel.component';
import { PageMetric } from '../../../shared/page-layout/page-layout.models';
import { CompanyScopeSelection, CompanyScopeSelectorComponent } from '../../company-scope-selector/company-scope-selector.component';
import { CRM_RECEIVABLE_ROLES, CrmPartyContextService } from '../../../shared/crm-client-contact-picker/crm-party-context.service';
import { LocaleService } from '../../../i18n/locale.service';
import { finalize } from 'rxjs';
import { PendingActionTracker } from '../../../shared/state/pending-action-tracker';


@Component({
    selector: 'app-generated-receipts',
    templateUrl: './generated-receipts.component.html',
    styleUrls: ['../../finance-list.shared.css', './generated-receipts.component.css'],
    providers: [
        MessageService,
        AccountsService,
        SessionStorageService,
        PaymentService
    ],
    standalone: true,
    imports: [FinancePdfViewButtonComponent,
        CommonModule,
        FormsModule,
        BreadcrumbModule,
        TableModule,
        MultiSelectModule,
        DatePickerModule,
        InputTextModule,
        ButtonModule,
        CardModule,
        TooltipModule,
        TagModule,
        ToastModule,
        ProgressSpinnerModule,
        ProgressBarModule,
        PanelModule,
        ImageModule,
        InputGroupModule,
        InputGroupAddonModule,
        IftaLabelModule,
        DrawerModule,
        PageShellComponent,
        PageHeroComponent,
        PageMetricStripComponent,
        PageSearchPanelComponent,
        PageDataPanelComponent,
        AccountingDirectoryControlsComponent,
        CompanyScopeSelectorComponent
    ]
})
export class GeneratedReceiptsComponent implements OnInit {
    readonly pendingStatuses = ['DRAFT', 'SUBMITTED', 'APPROVED', 'ON_HOLD'];
    selectedDocumentStatuses: string[] = this.pendingStatuses.slice();
    searchText = '';
    directoryMode: 'LIST' | 'CRM' | 'PARTY_TYPE' = 'LIST';
    directoryFilters: Record<string, string | number | boolean | number[] | string[] | null | undefined> = {};
    private listRequestId = 0;

    readonly documentActions = new PendingActionTracker<number | undefined>();
    private readonly localeService = inject(LocaleService);

    downloadReceipt(receipt: Receipt) {
        if (!receipt.receiptId || !this.documentActions.begin(receipt.receiptId)) return;
        this.accountsService.downloadReceiptDocument(receipt.receiptId).pipe(
            finalize(() => this.documentActions.end(receipt.receiptId))
        ).subscribe({
            next: pdf => this.accountsService.downloadPdfDocument(pdf, 'receipt', receipt.receiptNumber, receipt.receiptId, receipt.postingStatus),
            error: err => this.messageService.add({
                severity: 'error',
                summary: this.t('pages.generatedReceipts.messages.noPdfSummary'),
                detail: err?.error?.message || this.t('pages.generatedReceipts.messages.noPdfDetail')
            })
        });
    }

    homeBreadcrumb: MenuItem = { icon: 'pi pi-home', routerLink: '/' };
    breadcrumbItems: MenuItem[] = [];
    isLoading = false;
    first = 0;
    rows = 20;

    // Data properties
    allReceipts: Receipt[] = [];
    selectedReceipts: Receipt[] = [];
    totalRecords: number = 0;

    // Filter properties
    clients: any[] = [];
    selectedClients: any[] = [];
    dateRange: (Date | null)[] | null = null;
    selectedStatuses: string[] = [];
    receiptNumberFilter: string | null = null;
    paymentTransactionNumberFilter: string | null = null;

    // Summary Card properties
    totalUnappliedAmount: number = 0;
    totalAppliedAmount: number = 0;
    reportCurrencyCode: string = 'USD';

    canCreateReceipt = false;
    canApplyReceipt = false;
    canRegisterPayment = false;
    previewReceipt: Receipt | null = null;
    isPreviewVisible = false;
    currentScope: CompanyScopeSelection | null = null;

    get isConsolidatedReadOnly(): boolean {
        return !!this.currentScope?.readOnly;
    }

    constructor(
        private accountsService: AccountsService,
        private messageService: MessageService,
        private paymentService: PaymentService,
        private router: Router,
        private sessionStorageService: SessionStorageService,
        private permissionService: PermissionService,
        public crmPartyContext: CrmPartyContextService
    ) { }

    ngOnInit(): void {
        this.canCreateReceipt = this.permissionService.hasPermission(app_permissions.accounts.receipt.create);
        this.canApplyReceipt = this.permissionService.hasPermission(app_permissions.accounts.receipt.edit);
        this.canRegisterPayment = this.permissionService.hasPermission(app_permissions.accounts.receipt.edit);

        this.breadcrumbItems = [
            { label: this.t('pages.generatedReceipts.breadcrumb.accounts') },
            { label: this.t('pages.generatedReceipts.breadcrumb.enterprise') },
            { label: this.t('pages.generatedReceipts.breadcrumb.receivables') },
            { label: this.t('pages.generatedReceipts.breadcrumb.generatedReceipts') }
        ];
        this.loadInitialData();
    }

    onScopeChange(scope: CompanyScopeSelection) {
        this.currentScope = scope;
        this.selectedReceipts = [];
        this.applyFilters();
    }

    loadInitialData() {
        this.isLoading = true;
        const companyId = this.sessionStorageService.getCompany()?.id;
        if (!companyId) {
            this.messageService.add({
                severity: 'error',
                summary: this.t('common.error'),
                detail: this.t('pages.generatedReceipts.messages.companyNotFound')
            });
            this.isLoading = false;
            return;
        }

        this.crmPartyContext.loadClients(companyId, CRM_RECEIVABLE_ROLES).subscribe(clients => {
            this.clients = clients.map(client => ({ ...client, name: this.crmPartyContext.clientName(client) }));
        });

        // Initial data load for the table and summary is triggered by applyFilters
        this.applyFilters();
    }

    loadData(event: TableLazyLoadEvent) {
        this.isLoading = true;
        const requestId = ++this.listRequestId;
        this.first = event.first ?? 0;
        this.rows = event.rows ?? 20;

        const sessionCompanyId = this.sessionStorageService.getCompany()?.id;
        const companyGroupId = this.currentScope?.companyGroupId;
        const companyId = companyGroupId ? undefined : (this.currentScope?.companyId ?? sessionCompanyId);
        if (!companyId && !companyGroupId) {
            this.isLoading = false;
            return;
        }

        const clientIds = this.selectedClients.map(c => Number(c.id?.entityId ?? c.id)).filter(Boolean);
        const startDate = this.dateRange?.[0] ? this.dateRange[0].toISOString() : undefined;
        const endDate = this.dateRange?.[1] ? this.dateRange[1].toISOString() : undefined;

        // Phase 1 debt: receipt allocation status is still derived from linked invoices in the current page payload.
        // Later phases must move status/open-amount filtering fully behind backend-authoritative allocation fields.
        this.directoryFilters = { companyId, companyGroupId, clientIds, startDate, endDate, statuses: this.selectedStatuses, receiptNumber: this.receiptNumberFilter, paymentTransactionNumber: this.paymentTransactionNumberFilter, searchText: this.searchText };

        this.accountsService.getGeneratedReceipts(
            this.first / this.rows,
            this.rows,
            companyId,
            clientIds,
            this.selectedStatuses,
            startDate,
            endDate,
            this.receiptNumberFilter ?? undefined,
            this.paymentTransactionNumberFilter ?? undefined,
            companyGroupId,
            this.selectedDocumentStatuses,
            this.searchText
        ).subscribe({
            next: (dataWrapper) => {
                if (requestId !== this.listRequestId) return;
                this.allReceipts = dataWrapper.items.map(r => {
                    const appliedAmount = this.getAppliedAmount(r);
                    const unappliedAmount = (r.amount || 0) - appliedAmount;
                    let status: 'APPLIED' | 'PARTIALLY_APPLIED' | 'UNAPPLIED';

                    if (unappliedAmount <= 0.01) { // Use a small epsilon for float comparison
                        status = 'APPLIED';
                    } else if (appliedAmount > 0) {
                        status = 'PARTIALLY_APPLIED';
                    } else {
                        status = 'UNAPPLIED';
                    }

                    return {
                        ...r,
                        status: status
                    };
                });
                this.totalRecords = dataWrapper.count;
                this.isLoading = false;
            },
            error: (err) => {
                if (requestId !== this.listRequestId) return;
                this.messageService.add({
                    severity: 'error',
                    summary: this.t('common.error'),
                    detail: this.t('pages.generatedReceipts.messages.couldNotLoad')
                });
                this.isLoading = false;
            }
        });

        // Fetch filtered summary
        this.accountsService.getGeneratedReceiptsSummary(
            companyId,
            clientIds,
            this.selectedStatuses,
            startDate,
            endDate,
            this.receiptNumberFilter ?? undefined,
            this.paymentTransactionNumberFilter ?? undefined,
            companyGroupId
        ).subscribe(summary => {
                if (requestId !== this.listRequestId) return;
            this.totalAppliedAmount = summary.totalAppliedAmount || 0;
            this.totalUnappliedAmount = summary.totalUnappliedAmount || 0;
            if (summary.currencyCode) {
                this.reportCurrencyCode = summary.currencyCode;
            }
        });
    }

    onDirectoryModeChange(mode: 'LIST' | 'CRM' | 'PARTY_TYPE'): void {
        this.directoryMode = mode;
        this.selectedReceipts = [];
    }

    onDirectorySearch(value: string): void {
        this.searchText = value;
        this.selectedReceipts = [];
        this.applyFilters();
    }

    showPendingStatuses(): void {
        this.selectedDocumentStatuses = this.pendingStatuses.slice();
        this.applyFilters();
    }

    showAllStatuses(): void {
        this.selectedDocumentStatuses = [];
        this.applyFilters();
    }

    applyFilters() {
        this.loadData({ first: 0, rows: this.rows });
    }

    clearFilters() {
        this.selectedDocumentStatuses = this.pendingStatuses.slice();
        this.selectedClients = [];
        this.dateRange = null;
        this.selectedStatuses = [];
        this.receiptNumberFilter = null;
        this.paymentTransactionNumberFilter = null;
        this.searchText = '';
        this.applyFilters();
    }

    get metrics(): PageMetric[] {
        this.localeService.locale();
        return [
            { label: this.t('pages.generatedReceipts.metrics.unappliedAmount'), value: this.formatMoney(this.totalUnappliedAmount), hint: this.t('pages.generatedReceipts.metrics.summaryHint'), tone: 'warn' },
            { label: this.t('pages.generatedReceipts.metrics.appliedAmount'), value: this.formatMoney(this.totalAppliedAmount), hint: this.t('pages.generatedReceipts.metrics.summaryHint'), tone: 'success' },
            { label: this.t('pages.generatedReceipts.metrics.selectedAmount'), value: this.formatMoney(this.getSelectedTotal()), hint: this.t('pages.generatedReceipts.metrics.selectedHint', { count: this.selectedReceipts.length }), tone: 'info' },
            { label: this.t('pages.generatedReceipts.metrics.records'), value: this.totalRecords, hint: this.t('pages.generatedReceipts.metrics.backendCount') }
        ];
    }

    get statuses(): { label: string, value: string }[] {
        this.localeService.locale();
        return [
            { label: this.t('pages.generatedReceipts.status.applied'), value: 'APPLIED' },
            { label: this.t('pages.generatedReceipts.status.partiallyApplied'), value: 'PARTIALLY_APPLIED' },
            { label: this.t('pages.generatedReceipts.status.unapplied'), value: 'UNAPPLIED' }
        ];
    }

    get activeFilterCount(): number {
        return [
            this.selectedClients.length,
            this.dateRange?.some(Boolean) ? 1 : 0,
            this.selectedStatuses.length,
            this.receiptNumberFilter ? 1 : 0,
            this.paymentTransactionNumberFilter ? 1 : 0
        ].reduce((total, value) => total + Number(value), 0);
    }

    get resultText(): string {
        this.localeService.locale();
        return this.t('pages.generatedReceipts.results.receipts', { count: this.totalRecords });
    }

    get currentPageReportTemplate(): string {
        this.localeService.locale();
        return this.t('pages.generatedReceipts.table.pageReport');
    }

    getSelectedTotal(): number {
        return this.selectedReceipts.reduce((total, receipt) => total + (receipt.amount || 0), 0);
    }

    formatMoney(amount: number | undefined, currencyCode: string = this.reportCurrencyCode): string {
        return `${currencyCode || ''} ${Number(amount || 0).toLocaleString(undefined, { minimumFractionDigits: 4, maximumFractionDigits: 4 })}`;
    }

    private errorMessage(error: any, fallback: string): string {
        return error?.error?.message || error?.message || fallback;
    }

    statusLabel(status: string | undefined): string {
        this.localeService.locale();
        if (status === 'APPLIED') return this.t('pages.generatedReceipts.status.applied');
        if (status === 'PARTIALLY_APPLIED') return this.t('pages.generatedReceipts.status.partiallyApplied');
        if (status === 'UNAPPLIED') return this.t('pages.generatedReceipts.status.unapplied');
        return this.t('pages.generatedReceipts.status.unknown');
    }

    statusSeverity(status: string | undefined): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
        if (status === 'APPLIED') return 'success';
        if (status === 'PARTIALLY_APPLIED') return 'warn';
        if (status === 'UNAPPLIED') return 'danger';
        if (status === 'POSTED' || status === 'APPROVED' || status === 'CLOSED') return 'success';
        if (status === 'DRAFT' || status === 'SUBMITTED') return 'info';
        if (status === 'ON_HOLD') return 'warn';
        if (status === 'CANCELLED' || status === 'VOIDED' || status === 'REVERSED') return 'danger';
        return 'secondary';
    }

    openPreview(receipt: Receipt) {
        this.previewReceipt = receipt;
        this.isPreviewVisible = true;
    }

    canEditDraft(receipt: Receipt): boolean {
        return !this.isConsolidatedReadOnly && !!receipt.receiptId
            && this.permissionService.hasPermission(app_permissions.accounts.receipt.edit)
            && isEditableFinancialDraft(receipt, this.sessionStorageService.getCompany()?.id);
    }

    editDraft(receipt: Receipt): void {
        if (this.canEditDraft(receipt)) this.router.navigate(['/accounts/enterprise/receivables/generated-receipts/edit', receipt.receiptId]);
    }

    viewReceipt(receipt: Receipt) {
        if (receipt.receiptId) {
            this.router.navigate(['/accounts/enterprise/receivables/generated-receipts/view', receipt.receiptId]);
        }
    }

    exportToExcel() {
        if (this.allReceipts.length === 0) {
            this.messageService.add({
                severity: 'warn',
                summary: this.t('pages.generatedReceipts.messages.noDataSummary'),
                detail: this.t('pages.generatedReceipts.messages.noDataDetail')
            });
            return;
        }
        const worksheet = XLSX.utils.json_to_sheet(this.allReceipts.map(r => ({
            Client: this.crmPartyContext.displayName(r),
            Contact: this.crmPartyContext.contactDisplayName(r),
            'Receipt #': r.receiptNumber,
            'Receipt Date': r.receiptDate,
            'Total Amount': r.amount,
            'Applied Amount': this.getAppliedAmount(r),
            'Un-Applied Amount': this.getUnAppliedAmount(r),
            Currency: r.country?.currencyCode,
            Status: r.status,
            'Linked Invoices': r.invoices?.map(inv => inv.invoiceNumber).join(', '),
            'Payment Txn #': r.paymentTransactionNumber
        })));
        const workbook = { Sheets: { 'data': worksheet }, SheetNames: ['data'] };
        const excelBuffer: any = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        this.saveAsExcelFile(excelBuffer, 'GeneratedReceiptsExport');
    }

    private saveAsExcelFile(buffer: any, fileName: string): void {
        const data: Blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' });
        const url = window.URL.createObjectURL(data);
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName + '_' + new Date().getTime() + '.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
    }

    generatePaymentFromReceipts() {
        if (this.isConsolidatedReadOnly) {
            this.messageService.add({
                severity: 'warn',
                summary: this.t('pages.generatedReceipts.messages.readOnlyScopeSummary'),
                detail: this.t('pages.generatedReceipts.messages.registerPaymentsReadOnlyDetail')
            });
            return;
        }
        this.isLoading = true;
        if (!this.selectedReceipts || this.selectedReceipts.length === 0) {
            this.messageService.add({
                severity: 'error',
                summary: this.t('pages.generatedReceipts.messages.invalidSelectionSummary'),
                detail: this.t('pages.generatedReceipts.messages.noReceiptsSelected')
            });
            this.isLoading = false;
            return;
        }
        if (this.selectedReceipts.some(r => r.paymentTransactionNumber != null)) {
            this.messageService.add({
                severity: 'error',
                summary: this.t('pages.generatedReceipts.messages.invalidSelectionSummary'),
                detail: this.t('pages.generatedReceipts.messages.alreadyPaidReceipts')
            });
            this.isLoading = false;
            return;
        }
        this.paymentService.generatePaymentFromReceipts(this.selectedReceipts, this.sessionStorageService.getCustomer()?.firebaseId ?? '').subscribe({
            next: (paymentTransaction: PaymentTransactions) => {
                console.log('Payment Transaction from Receipts:', paymentTransaction);
                this.messageService.add({
                    severity: 'success',
                    summary: this.t('common.success'),
                    detail: this.t('pages.generatedReceipts.messages.paymentGenerated')
                });
                this.applyFilters(); // Refresh data
            },
            error: (error) => {
                console.error('Error generating payment:', error);
                this.messageService.add({
                    severity: 'error',
                    summary: this.t('common.error'),
                    detail: this.errorMessage(error, this.t('pages.generatedReceipts.messages.paymentGenerateFailed'))
                });
                this.isLoading = false;
            }
        });
    }

    createReceipt() {
        if (this.isConsolidatedReadOnly) {
            this.messageService.add({
                severity: 'warn',
                summary: this.t('pages.generatedReceipts.messages.readOnlyScopeSummary'),
                detail: this.t('pages.generatedReceipts.messages.createReceiptsReadOnlyDetail')
            });
            return;
        }
        this.router.navigate(['/accounts/enterprise/receivables/generated-receipts/add']);
    }

    applyReceiptOnInvoice() {
        if (this.isConsolidatedReadOnly) {
            this.messageService.add({
                severity: 'warn',
                summary: this.t('pages.generatedReceipts.messages.readOnlyScopeSummary'),
                detail: this.t('pages.generatedReceipts.messages.applyReceiptsReadOnlyDetail')
            });
            return;
        }
        if (!this.selectedReceipts || this.selectedReceipts.length === 0) {
            this.messageService.add({
                severity: 'error',
                summary: this.t('common.error'),
                detail: this.t('pages.generatedReceipts.messages.selectReceiptToApply')
            });
            return;
        }
        if (this.selectedReceipts.length > 1) {
            this.messageService.add({
                severity: 'error',
                summary: this.t('common.error'),
                detail: this.t('pages.generatedReceipts.messages.selectOnlyOneReceipt')
            });
            return;
        }
        const receipt = this.selectedReceipts[0];
        if (this.getUnAppliedAmount(receipt) <= 0) {
            this.messageService.add({
                severity: 'warn',
                summary: this.t('pages.generatedReceipts.messages.alreadyAppliedSummary'),
                detail: this.t('pages.generatedReceipts.messages.receiptAlreadyApplied')
            });
            return;
        }
        this.router.navigate(['/accounts/enterprise/receivables/apply-receipts-invoice', receipt.receiptId]);
    }

    getAppliedAmount(receipt: Receipt): number {
        return receipt.allocatedAmount ?? receipt.invoices?.reduce((acc, invoice) => acc + (invoice.allocatedAmount ?? invoice.totalAmount ?? 0), 0) ?? 0;
    }

    getUnAppliedAmount(receipt: Receipt): number {
        return receipt.openAmount ?? Math.max(0, (receipt.amount || 0) - this.getAppliedAmount(receipt));
    }

    t(key: string, fallbackOrParams?: string | Record<string, unknown>, params?: Record<string, unknown>): string {
        if (typeof fallbackOrParams === 'string') {
            return this.localeService.translateIfPresent(key, fallbackOrParams, params);
        }
        return this.localeService.translate(key, fallbackOrParams);
    }
}
